import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { AuthUser } from '../types';
import { API_BASE_URL } from '../config/api';
import { clearPreferenceCache } from '../services/uxService';

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isCashier: boolean;
  login: (token: string, user: AuthUser) => void;
  logout: () => void;
  hasPermission: (claim: string) => boolean;
  hasAnyPermission: (claims: string[]) => boolean;
}

export const AuthContext = createContext<AuthContextType | null>(null);

function loadFromStorage(): { token: string | null; user: AuthUser | null } {
  try {
    const token = localStorage.getItem('auth_token');
    const raw = localStorage.getItem('user');
    const user = raw ? (JSON.parse(raw) as AuthUser) : null;
    return { token, user };
  } catch {
    return { token: null, user: null };
  }
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const initial = loadFromStorage();
  const [token, setToken] = useState<string | null>(initial.token);
  const [user, setUser] = useState<AuthUser | null>(initial.user);

  useEffect(() => {
    if (!token || !user) return;
    fetch(`${API_BASE_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((json) => {
        const freshUser = json?.data?.user;
        if (freshUser) {
          localStorage.setItem('user', JSON.stringify(freshUser));
          setUser(freshUser);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback((newToken: string, newUser: AuthUser) => {
    localStorage.setItem('auth_token', newToken);
    localStorage.setItem('user', JSON.stringify(newUser));
    setToken(newToken);
    setUser(newUser);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('company'); // module context — next login repopulates it
    localStorage.removeItem('last_app'); // app switcher state — next user picks their own default
    clearPreferenceCache(); // tills are shared — next login fetches that user's tour state
    setToken(null);
    setUser(null);
  }, []);

  const hasPermission = useCallback(
    (claim: string) => user?.permissions?.includes(claim) ?? false,
    [user]
  );

  const hasAnyPermission = useCallback(
    (claims: string[]) => claims.some((c) => user?.permissions?.includes(c) ?? false),
    [user]
  );

  const isCashier = !!(
    user?.roles?.some((r) => r.name === 'Cashier') &&
    !user?.roles?.some((r) => r.name === 'Admin' || r.name === 'Manager')
  );

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated: !!token && !!user,
        isCashier,
        login,
        logout,
        hasPermission,
        hasAnyPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
